import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

// Get environment variables with fallbacks
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Enhanced validation for Supabase configuration
if (!supabaseUrl) {
  console.error('Missing Supabase URL. Please check your .env configuration.');
}

if (!supabaseAnonKey) {
  console.error('Missing Supabase Anon Key. Please check your .env configuration.');
}

console.log('Initializing Supabase client with:', {
  hasUrl: !!supabaseUrl,
  hasKey: !!supabaseAnonKey,
  urlPrefix: supabaseUrl ? supabaseUrl.substring(0, 10) + '...' : 'undefined',
  keyPrefix: supabaseAnonKey ? supabaseAnonKey.substring(0, 5) + '...' : 'undefined',
  fullKeyLength: supabaseAnonKey ? supabaseAnonKey.length : 0
});

// Create Supabase client with improved configuration
export const supabase = createClient<Database>(
  supabaseUrl || '',
  supabaseAnonKey || '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: window.localStorage,
    storageKey: 'supabase.auth.token',
    debug: import.meta.env.DEV,
    retryAttempts: 3,
    retryInterval: 1000
  },
  global: {
    headers: {
      'X-Client-Info': 'dochelper-ai@1.0.0',
      'apikey': supabaseAnonKey || '',
      'Content-Type': 'application/json'
    },
    fetch: (url, options = {}) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      return fetch(url, {
        ...options,
        signal: controller.signal,
      }).finally(() => {
        clearTimeout(timeoutId);
      });
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    timeout: 10000,
    retryAttempts: 3,
    retryInterval: 1000
  }
});

// Enhanced connection state management
let isConnected = false;
let connectionError: Error | null = null;
let lastCheckTime = 0;
const CHECK_INTERVAL = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const CONNECTION_TIMEOUT = 10000; // 10 seconds

// Create a timeout wrapper for Supabase queries with better error handling
export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = CONNECTION_TIMEOUT): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error(`Request timed out after ${timeoutMs}ms`)), timeoutMs)
    )
  ]);
};

// Enhanced connection check with better error diagnostics
export const checkConnection = async (forceCheck = false) => {
  const now = Date.now();
  console.log('Connection check requested, force:', forceCheck);
  
  // Only check if forced or enough time has passed since last check
  if (!forceCheck && now - lastCheckTime < CHECK_INTERVAL) {
    console.log('Using cached connection status:', isConnected);
    return isConnected;
  }

  let retries = 0;
  while (retries < MAX_RETRIES) {
    try {
      // First check network connectivity
      if (!navigator.onLine) {
        console.log('Device is offline');
        throw new Error('You are currently offline. Please check your internet connection.');
      }

      // Test basic connectivity to Supabase first
      try {
        // Skip health check if we don't have valid credentials
        if (!supabaseUrl || !supabaseAnonKey) {
          console.error('Missing Supabase credentials');
          throw new Error('Invalid Supabase configuration. Please check your VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env file.');
        }
        
        const headers = {
          'apikey': supabaseAnonKey,
        };
        
        const healthCheck = await withTimeout(
          fetch(`${supabaseUrl}/rest/v1/`, {
            method: 'HEAD',
            headers
          }),
          5000
        );

        if (!healthCheck.ok) {
          if (healthCheck.status === 401) {
            // This is actually OK in many cases - it means the API key is valid but doesn't have permissions
            // We'll continue with the database check to confirm
            console.warn('Got 401 from health check, but this might be due to RLS policies');
          } else if (healthCheck.status === 404) {
            console.error('Supabase project not found:', supabaseUrl);
            throw new Error('Supabase project not found. Please check your VITE_SUPABASE_URL in .env file.');
          } else if (healthCheck.status >= 500) {
            console.error('Supabase server error:', healthCheck.status);
            throw new Error('Supabase servers are experiencing issues. Please try again later.');
          } else {
            console.error('Unexpected Supabase error:', healthCheck.status);
            throw new Error(`Supabase server error: ${healthCheck.status}`);
          }
        }
      } catch (fetchError: any) {
        if (fetchError.name === 'AbortError' || fetchError.message.includes('timed out')) {
          throw new Error('Connection timed out. Please check your internet connection.');
        } else if (fetchError.message.includes('Failed to fetch')) {
          throw new Error('Cannot reach Supabase servers. Please check your internet connection and Supabase project status.');
        }
        throw fetchError;
      }

      // Then check database connection with a simple query
      try {
        const { data, error: dbError } = await withTimeout(
          supabase.from('documents').select('count').limit(1),
          8000
        );

        if (dbError) {
          // If we get a permission error, that's actually OK - it means the connection works
          // but we don't have permission to access the users table due to RLS
          if (dbError.message?.includes('permission') || dbError.message?.includes('RLS')) {
            console.warn('Permission denied for documents table, but connection is working');
            // This is a successful connection, just with expected permission limitations
            isConnected = true;
            connectionError = null;
            lastCheckTime = Date.now();
            return true;
          }
          
          if (dbError.code === 'PGRST116') {
            throw new Error('Database schema issue: "documents" table not found.');
          } else if (dbError.code === 'PGRST301') {
            console.error('Database connection failed:', dbError);
            throw new Error('Database connection failed. Please check your Supabase project status.');
          } else if (dbError.message?.includes('JWT')) {
            console.error('Authentication error:', dbError);
            throw new Error('Authentication error. Please check your API key configuration.');
          } else {
            console.error('Database error:', dbError);
            throw new Error(`Database error: ${dbError.message}`);
          }
        }
      } catch (dbError: any) {
        // If the error is about permissions, that's actually OK
        if (dbError.message?.includes('permission') || dbError.message?.includes('RLS')) {
          console.warn('Permission denied for documents table, but connection is working');
          // This is a successful connection, just with expected permission limitations
          isConnected = true;
          connectionError = null;
          lastCheckTime = Date.now();
          return true;
        }
        throw dbError;
      }

      isConnected = true;
      connectionError = null;
      lastCheckTime = Date.now();
      console.log('Connection check successful');
      return true;
    } catch (err: any) {
      retries++;
      if (retries === MAX_RETRIES) {
        isConnected = false;
        connectionError = err;
        lastCheckTime = now;
        console.error('Connection check failed:', err);
        return false;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    }
  }

  return false;
};

export const getConnectionState = () => ({
  isConnected,
  connectionError,
  lastCheckTime,
  isStale: Date.now() - lastCheckTime > CHECK_INTERVAL
});

// Add connection recovery with better error handling
export const recoverConnection = async () => {
  const recoveryAttempts = 3;
  for (let i = 0; i < recoveryAttempts; i++) {
    const isRecovered = await checkConnection(true);
    if (isRecovered) return true;
    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (i + 1)));
  }
  return false;
};

// Initialize auth state from URL if present
export const initializeAuthFromUrl = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error) {
    console.error('Error initializing auth from URL:', error);
    return null;
  }
};

// Export configuration for debugging
export const getSupabaseConfig = () => ({
  url: supabaseUrl || 'not set',
  hasValidKey: Boolean(supabaseAnonKey),
  keyPrefix: supabaseAnonKey ? supabaseAnonKey.substring(0, 5) + '...' : 'missing or invalid'
});